<script setup lang="ts">
definePageMeta({
  layout: false
})

useHead({
  title: 'Connexion | Riven Bot Admin'
})

const route = useRoute()
const router = useRouter()
const config = useRuntimeConfig()
const { isAuthenticated } = useSanctumAuth()

// Compute destination redirect path
const targetRedirect = computed(() => {
  const queryRedirect = route.query.redirect as string
  if (queryRedirect && queryRedirect.startsWith('/') && !queryRedirect.startsWith('/login')) {
    return queryRedirect
  }
  return '/'
})

// Error banner mapping matching Pandaahhdmin
const errorNotice = computed(() => {
  const err = route.query.error as string
  if (err === 'no_discord_linked') {
    return "Votre compte utilisateur n'est associé à aucun identifiant Discord. Veuillez vous connecter avec Discord."
  }
  if (err === 'not_discord_admin') {
    return "Accès refusé : votre compte Discord ne possède pas le rôle requis (Administrateur ou Staff) sur le serveur Pandaahh."
  }
  if (err === 'auth_failed') {
    return 'Échec de la validation de session Discord. Veuillez réessayer.'
  }
  if (err === 'unauthorized') {
    return 'Vous devez être connecté pour accéder à cette interface.'
  }
  return ''
})

const oauthRedirectUrl = computed(() => {
  if (import.meta.client) {
    return window.location.origin
  }
  return 'http://localhost:3012'
})

const loginWithDiscord = () => {
  try {
    localStorage.setItem('auth_redirect', targetRedirect.value)
  } catch {}

  const url = `${config.public.apiUrl}/auth/discord/redirect?redirect_url=${encodeURIComponent(oauthRedirectUrl.value)}&app=bot-riven`
  window.location.href = url
}

// Redirect if already authenticated
watchEffect(() => {
  if (isAuthenticated.value && !route.query.error) {
    router.push(targetRedirect.value)
  }
})
</script>

<template>
  <div class="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-4 relative overflow-hidden selection:bg-primary selection:text-[#0E1729]">
    <!-- Background Content matching Screen 2 (Pandaahhdmin) -->
    <div class="absolute inset-0 bg-[url('/background.webp')] bg-cover bg-center pointer-events-none opacity-40"></div>
    <div class="absolute inset-0 bg-black/60 pointer-events-none"></div>
    <div class="absolute top-0 left-0 w-full h-full bg-linear-to-b from-black via-transparent to-black pointer-events-none"></div>

    <!-- Main Card matching Screen 2 -->
    <div class="relative w-full max-w-xl bg-[#131b2e]/60 backdrop-blur-xl border border-white/5 rounded-2xl px-6 pb-8 sm:px-10 sm:pb-10 pt-20 sm:pt-[84px] text-center shadow-2xl">
      <!-- Floating Logo -->
      <div class="absolute -top-16 left-1/2 -translate-x-1/2">
        <div class="size-32 rounded-full bg-[#0a0f1c] border border-[#d4b483]/20 p-1 flex items-center justify-center shadow-[0_0_30px_rgba(212,180,131,0.15)]">
          <div class="size-full rounded-full overflow-hidden flex items-center justify-center bg-black">
            <img src="/logo.webp" alt="Pandaahh Logo" class="h-full w-full object-cover" />
          </div>
        </div>
      </div>

      <!-- Icon Badge -->
      <div class="flex justify-center mb-6">
        <div class="size-12 rounded-full bg-white/5 flex items-center justify-center border border-white/5">
          <UIcon
            name="i-fa6-brands-discord"
            class="text-[#5865F2] text-2xl"
          />
        </div>
      </div>

      <!-- Content -->
      <div class="space-y-6">
        <div>
          <h1 class="font-bold text-3xl sm:text-5xl text-white uppercase tracking-wider leading-tight mb-4 drop-shadow-xl font-display">
            RIVEN BOT ADMIN
          </h1>
          <div class="h-px w-24 bg-linear-to-r from-transparent via-[#d4b483] to-transparent mx-auto opacity-70"></div>
        </div>

        <p class="text-gray-300/80 text-base sm:text-lg leading-relaxed max-w-md mx-auto font-light">
          Administration Discord &bull; Gestion des Salons & Mods Riven
        </p>

        <!-- Error Banner -->
        <div v-if="errorNotice" class="p-3.5 bg-red-950/40 border border-red-500/40 rounded-xl text-xs text-red-300 flex items-start gap-2.5 text-left max-w-md mx-auto">
          <UIcon name="i-heroicons-exclamation-triangle" class="size-5 shrink-0 text-red-400 mt-0.5" />
          <p>{{ errorNotice }}</p>
        </div>

        <!-- Action Button: Discord Only -->
        <div class="pt-4 flex items-center justify-center">
          <button
            type="button"
            @click="loginWithDiscord"
            class="w-full sm:w-auto bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold uppercase tracking-wider px-8 py-3.5 rounded-lg transition-all shadow-[0_0_20px_rgba(88,101,242,0.3)] hover:shadow-[0_0_30px_rgba(88,101,242,0.5)] transform hover:-translate-y-0.5 flex items-center justify-center gap-3 cursor-pointer text-sm sm:text-base active:scale-[0.98]"
          >
            <UIcon name="i-fa6-brands-discord" class="text-xl" />
            <span>Connexion avec Discord</span>
          </button>
        </div>

        <!-- Return / Ecosystem Text -->
        <div class="pt-4 space-y-1.5">
          <p class="text-xs text-gray-400/80 font-normal">
            Accès restreint aux membres du serveur Pandaahh possédant le rôle Administrateur ou Staff.
          </p>
          <p class="text-[10px] uppercase tracking-[0.3em] text-gray-500/80 font-bold">
            Écosystème Pandaahh
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
